import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sqWorkbenchStore } from '@/core/core.stores';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { useModalManager } from '@/core/hooks/useModalManager.hook';
import { DatasourcesStatus } from '@/footer/DatasourcesStatus.organism';
import { MopinionGetFeedback } from '@/getFeedback/GetFeedback.atom';
import { HelpCenterModal } from '@/helpCenter/HelpCenterModal';
import { isAdmin, isTrustedUser } from '@/services/authorization.service';
import { seeqFullReleaseIdentifier } from '@/services/systemConfiguration.utilities';
import { SUPPORT_LINK } from '@/utilities/externalLinks';
import { subscribe } from '@/utilities/socket.utilities';
import { SeeqNames } from '@seeqdev/constants';
import { ButtonWithPopover, ExternalLink, Icon } from '@seeqdev/qomponents';

export interface RequestProgressMessage {
  consumption: string;
  serverLoad: {
    description: string;
  };
}

export const Footer = () => {
  const { t } = useTranslation();
  const [threadPoolUsage, setThreadPoolUsage] = useState('');
  const [showHelpCenterModal, setShowHelpCenterModal] = useModalManager(HelpCenterModal);
  const interactiveSessionId = useFluxPath(sqWorkbenchStore, () => sqWorkbenchStore.interactiveSessionId);
  const isDev = process.env.NODE_ENV === 'development';

  useEffect(() => {
    return subscribe({
      channelId: [SeeqNames.Channels.RequestsProgress, sqWorkbenchStore.interactiveSessionId],
      onMessage: (message: RequestProgressMessage) =>
        setThreadPoolUsage(`${message.serverLoad.description} ${message.consumption}`),
      useSubscriptionsApi: false,
    });
  }, [interactiveSessionId]);

  return (
    <footer id="footer" data-testid="footer" className="mainFooter flexColumnContainer hideInPdf tw:pr-sq-10">
      <MopinionGetFeedback />

      <div
        data-testid="feedback"
        className="cursorPointer sq-call-out tw:mr-sq-10 tw:pt-1 tw:pr-sq-10 tw:pb-1 tw:pl-sq-10"
        onClick={() => setShowHelpCenterModal(true)}>
        <Icon icon="fa-comment-dots" extraClassNames="mr5" type="white" />
        {t('FEEDBACK')}
      </div>
      <div id="seeqVersion" className="inlineBlock tw:pt-1 tw:pb-1">
        {seeqFullReleaseIdentifier()} {isDev && '[DEV]'}
      </div>
      <div className="flexColumnContainer autoMarginLeft">
        {isTrustedUser() && <DatasourcesStatus />}

        {isAdmin() && !_.isEmpty(threadPoolUsage) && (
          <ButtonWithPopover
            placement="top"
            hasArrow
            tooltipPlacement="top"
            tooltip={t('THREAD_POOL_USAGE.TOOLTIP')}
            trigger={
              <div className="flexColumnContainer cursorPointer tw:items-center tw:whitespace-nowrap">
                <span className="tw:mr-sq-5 tw:ml-sq-5 tw:cursor-default tw:text-sm">|</span>
                <span className="tw:pr-sq-5 tw:text-sm">{t('THREAD_POOL_USAGE.TITLE')}</span>
              </div>
            }>
            <div id="threadPoolUsagePopOver" className="datasourcesPopup tw:min-w-sq-500! tw:pt-sq-10">
              <div>
                <div className="flexColumnContainer tw:items-center tw:justify-center">
                  <div className="text-monospace sq-text-color tw:whitespace-pre-wrap">{threadPoolUsage}</div>
                </div>
                <div className="text-center tw:pt-sq-7 tw:pb-sq-7">
                  <ExternalLink
                    href={`${SUPPORT_LINK}/troubleshooting-performance#TroubleshootingPerformance-ThreadPoolUsagePop-Up`}>
                    <span>{t('THREAD_POOL_USAGE.KB_LINK_DESCRIPTION')}</span>
                  </ExternalLink>
                </div>
              </div>
            </div>
          </ButtonWithPopover>
        )}
      </div>
      {showHelpCenterModal && <HelpCenterModal onClose={() => setShowHelpCenterModal(false)} />}
    </footer>
  );
};
